
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Edit } from 'lucide-react';
import { supabase } from '../../../integrations/supabase/client';
import type { Database } from '../../../integrations/supabase/types';

type Vehicle = Database['public']['Tables']['vehicles']['Row'];

const Vehicles = () => {
  const { data: vehicles, isLoading, error } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*');
      
      if (error) {
        console.error('Error fetching vehicles:', error);
        throw error;
      }
      return data as Vehicle[];
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    console.error('Vehicles error:', error);
    return (
      <div className="text-center py-4">
        <p className="text-gray-600">Unable to load vehicles. Please try again later.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Manage Vehicles</h2>
        <Link
          to="/admin/vehicles/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Vehicle
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vehicles?.map((vehicle) => (
          <div key={vehicle.id} className="bg-white rounded-lg shadow p-6">
            <img 
              src={vehicle.image_url || ''} 
              alt={vehicle.name}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-semibold">{vehicle.name}</h3>
              <Link
                to={`/admin/vehicles/${vehicle.id}`}
                className="inline-flex items-center text-gray-600 hover:text-primary"
              >
                <Edit className="w-5 h-5" />
              </Link>
            </div>
            <p className="text-gray-600 mb-4">{vehicle.description}</p>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Price per km</span>
                <span>€{vehicle.price_per_km}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Type</span>
                <span className="capitalize">{vehicle.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Capacity</span>
                <span>{vehicle.capacity} passengers, {vehicle.luggage_capacity} luggage</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Vehicles;
